import React, { useContext, useState } from 'react';

import { Theme, Fade } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import compact from 'lodash/compact';
import { observer } from 'mobx-react-lite';

import { colors } from '../../constants/theme';
import { TableContext } from '../../contexts/TableContext';
import Badges from '../Badges';
import SpeedDial from '../SpeedDial';

const useStyles = makeStyles((theme: Theme) => {
  return {
    user: {
      border: `1px solid ${colors.secondary}`,
    },
    paper: {
      width: '100%',
      marginLeft: 4,
      marginBottom: 4,
      minHeight: 24,
      padding: 8,
      overflow: 'hidden',
      position: 'relative',
      zIndex: 1,
      background: colors.grayLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      [theme.breakpoints.up('sm')]: {
        flex: '1 1 148px',
      },
    },
    paperLeft: {
      display: 'flex',
      alignItems: 'center',
      height: 24,
    },
    paperRight: {
      display: 'flex',
      alignItems: 'center',
    },
    stack: {
      marginRight: 8,
      fontFamily: 'Roboto Condensed',
      color: colors.grayMid,
    },
    chip_root: {
      '& $chip_avatarColorSecondary': {
        backgroundColor: 'transparent',
      },
    },
    chip_avatarColorSecondary: {},
  };
});

const Spectator = observer(
  ({
    id,
    hasAdd = false,
    isPending = false,
  }: {
    id: string;
    hasAdd: boolean;
    isPending?: boolean;
  }) => {
    const [openId, setOpenId] = useState('');
    const tableStore = useContext(TableContext)!;
    const userId = tableStore.table!.game.me.id;
    const spectator = tableStore.spectators![id];

    const classes = useStyles();

    const spectatorActions = compact([
      hasAdd && (
        <Chip
          size="small"
          color="secondary"
          label="Add to game"
          onClick={() => tableStore.addPlayerToTable(openId)}
          disabled={isPending}
          avatar={<AddIcon fontSize="small" />}
          classes={{
            root: classes.chip_root,
            avatarColorSecondary: classes.chip_avatarColorSecondary,
          }}
        />
      ),
    ]);

    return (
      <Paper
        key={id}
        elevation={0}
        className={clsx(classes.paper, {
          [classes.user]: id === userId,
        })}
        data-testid={`spectator_${spectator.name}`}
      >
        <Box className={classes.paperLeft}>{spectator.name}</Box>
        <Box className={classes.paperRight}>
          {!!spectator.stack && (
            <Fade in={!openId}>
              <Typography variant="body2" className={classes.stack}>
                {spectator.stack}
              </Typography>
            </Fade>
          )}
          <Badges id={spectator.id} />
          {!!spectatorActions.length && (
            // @ts-ignore SpeedDial
            <SpeedDial
              actions={spectatorActions}
              isOpen={openId === id}
              onClick={() => (openId === id ? setOpenId('') : setOpenId(id))}
              onClickAway={() => {
                openId === id && setOpenId('');
              }}
              OpenIcon={AddIcon}
            />
          )}
        </Box>
      </Paper>
    );
  }
);

export default Spectator;
